// Manual Products label (plan: feat-manual-products.md, decision 10):
// CODE128 of the new SKU, the SKU text, and the SUPPLIER underneath.
//
// It takes EXACTLY the route the SKU Finder package label takes — the
// agent's standard barcode layout (/print-barcode locally, a barcodeValue
// job on the network relay) — so it prints the same way up on every desk.
// The supplier travels as the `footer` option, which agents ≥ 1.6.3 draw as
// a small line under the value; older agents ignore it and print the
// barcode + value only. (The first cut sent raw ZPL, which agents before
// 1.6.0 refuse; the HTML-to-PDF fallback then printed down the label on the
// Newport desks, 2026-10-16.) Non-Zebra printers get the HTML label.
import { silentPrintBarcode, silentPrint, getSavedLocalPrinter } from "./printAgent";
import { submitNetworkPrintJob, getSavedNetworkPrinter } from "./networkPrint";
import type { PrintMode, PrintResult } from "./carrierLabelPrint";

export type ManualLabelOrientation = "normal" | "cw" | "ccw" | "flip";

export interface ManualLabelOpts {
    widthMm?: number;   // label stock width (default 65)
    heightMm?: number;  // label stock height (default 38)
    // HTML fallback only (non-Zebra printers). Default "normal", the same
    // unrotated page the package label uses on those desks.
    orientation?: ManualLabelOrientation;
}

/** HTML fallback for non-Zebra printers: same content as the ZPL layout.
 *  The landscape content is laid out at w × h in .rot and can be rotated a
 *  quarter turn (page footprint swapped) for a driver that needs it. */
export function buildManualProductLabelHtml(barcodeValue: string, supplier: string, opts: ManualLabelOpts = {}): string {
    const w = opts.widthMm && opts.widthMm > 0 ? opts.widthMm : 65;
    const h = opts.heightMm && opts.heightMm > 0 ? opts.heightMm : 38;
    const esc = (s: string) => String(s ?? "").replace(/[<>&"]/g, c => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "\"": "&quot;" }[c] as string));
    const value = esc(barcodeValue);
    const foot = esc(supplier);
    const rotDeg = { normal: 0, cw: 90, ccw: -90, flip: 180 }[opts.orientation ?? "normal"] ?? 0;
    const swap = rotDeg === 90 || rotDeg === -90;
    const pageW = swap ? h : w;
    const pageH = swap ? w : h;
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${pageW}mm ${pageH}mm; margin: 0; }
html, body { width: ${pageW}mm; height: ${pageH}mm; margin: 0; padding: 0; }
body { position: relative; overflow: hidden; }
.rot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${w}mm;
    height: ${h}mm;
    transform: translate(-50%, -50%) rotate(${rotDeg}deg);
    transform-origin: center center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: Arial, Helvetica, sans-serif;
    padding: 1mm;
}
svg { width: ${w - 5}mm !important; height: auto !important; max-height: ${Math.max(10, h - 16)}mm; }
.barcode-text { font-size: 18px; font-weight: 700; font-family: monospace; letter-spacing: 0.1em; margin-top: 2px; text-align: center; }
.supplier { font-size: 12px; font-weight: 700; text-transform: uppercase; margin-top: 1px; text-align: center; white-space: nowrap; overflow: hidden; max-width: 100%; }
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="rot">
<svg id="barcode"></svg>
<div class="barcode-text">${value}</div>
${foot ? `<div class="supplier">${foot}</div>` : ""}
</div>
<script>
JsBarcode("#barcode", "${value}", { format: "CODE128", width: 2, height: 70, displayValue: false, margin: 0 });
<\/script>
</body>
</html>`;
}

/** Print the label: the network relay when that is the active mode and a
 *  printer is saved (barcodeValue job + footer), else the local agent's
 *  /print-barcode (footer + HTML fallback for non-Zebra), else the HTML
 *  label straight to the local agent. */
export const printManualProductLabel = async (
    barcodeValue: string,
    supplier: string,
    mode: PrintMode,
    opts: ManualLabelOpts = {},
): Promise<PrintResult> => {
    const html = buildManualProductLabelHtml(barcodeValue, supplier, opts);
    const dims = {
        ...(opts.widthMm && opts.widthMm > 0 ? { labelWidthMm: opts.widthMm } : {}),
        ...(opts.heightMm && opts.heightMm > 0 ? { labelHeightMm: opts.heightMm } : {}),
    };
    const footer = supplier.trim();
    let r: PrintResult = { success: false };

    const saved = getSavedNetworkPrinter();
    if (mode === "network" && saved) {
        r = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, 1, barcodeValue, false, {
            ...(footer ? { footer } : {}), ...dims,
        });
        if (r.success) return r;
    }

    const local = await silentPrintBarcode(barcodeValue, 1, {
        ...(footer ? { footer } : {}), fallbackHtml: html, printer: getSavedLocalPrinter() ?? undefined, ...dims,
    });
    if (local.success) return local;

    const plain = await silentPrint(html, 1, getSavedLocalPrinter() ?? undefined);
    return plain.success ? plain : { success: false, error: plain.error || local.error || r.error };
};
